import { React } from 'react'
import { motion } from 'framer-motion'
import { useDocumentTitle, useDocumentConfig } from 'hooks/useDocument'
import Hero from 'modules/Hero'
import Button from 'components/Button'
import ServicesList from 'components/ServicesList'
import './ServicesPages.scss'

const ApplicationsWeb = () => {
	useDocumentTitle(`Applications Web Complexes - SPA, PWA et Solutions Sur-Mesure`)
	useDocumentConfig(`blue`, false)

	return (
		<motion.main
			className='service-page'
			initial={{ opacity: 0, transition: { duration: 0.3 } }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0, transition: { duration: 0.3 } }}
		>
			<Hero
				scene={2}
				scrollTo='Découvrir nos solutions'
			>
				Applications<br />
				web complexes
			</Hero>

			<section className='service-intro'>
				<div className='container'>
					<h1 className='align-center'>Applications Web Complexes</h1>
					<p className='lead align-center'>
						Développement d'applications web modernes, scalables et performantes pour digitaliser vos processus métiers
					</p>
				</div>
			</section>

			<section className='service-content'>
				<div className='container'>
					<div className='content-grid'>
						<div className='content-main'>
							<h2>Solutions web avancées pour projets ambitieux</h2>
							<p>
								Nous concevons et développons des applications web complexes qui répondent aux défis 
								techniques les plus exigeants. De la Single Page Application (SPA) à l'architecture 
								microservices, en passant par les Progressive Web Apps (PWA) et les solutions temps réel, 
								nous maîtrisons les technologies de pointe pour créer des applications robustes et évolutives.
							</p>

							<h3>Types d'applications développées</h3>
							<ul className='no-prefix'>
								<li><strong>Single Page Applications (SPA)</strong> : Interfaces réactives et expérience utilisateur fluide</li>
								<li><strong>Progressive Web Apps (PWA)</strong> : Applications installables, mode hors-ligne, notifications push</li>
								<li><strong>Dashboards et tableaux de bord</strong> : Visualisation de données en temps réel, KPI métiers</li>
								<li><strong>Plateformes SaaS</strong> : Applications multi-tenants, facturation intégrée, API REST/GraphQL</li>
								<li><strong>Applications métiers</strong> : CRM, ERP, gestion de projets, outils collaboratifs</li>
								<li><strong>Applications desktop (Electron)</strong> : Applications multiplateformes Windows/Mac/Linux</li>
							</ul>

							<h3>Stack technique moderne</h3>
							<div className='tech-grid'>
								<div className='tech-category'>
									<h4>Front-end avancé</h4>
									<p>React, Vue 3, Angular, Next.js, Nuxt 3, TypeScript, Redux/Vuex</p>
								</div>
								<div className='tech-category'>
									<h4>Back-end scalable</h4>
									<p>Node.js, NestJS, Express, Python/Django, Ruby on Rails, Go</p>
								</div>
								<div className='tech-category'>
									<h4>Bases de données</h4>
									<p>PostgreSQL, MongoDB, Redis, Elasticsearch, TimescaleDB</p>
								</div>
								<div className='tech-category'>
									<h4>Applications desktop</h4>
									<p>Electron, Tauri, distribution multi-OS, auto-updates</p>
								</div>
								<div className='tech-category'>
									<h4>Infrastructure cloud</h4>
									<p>AWS, Google Cloud, Docker, Kubernetes, CI/CD, Terraform</p>
								</div>
							</div>

							<h3>Fonctionnalités avancées maîtrisées</h3>
							<ul className='no-prefix'>
								<li><strong>Temps réel</strong> : WebSockets, Server-Sent Events, notifications push</li>
								<li><strong>Architecture microservices</strong> : Services découplés, API Gateway, event-driven</li>
								<li><strong>Server-Side Rendering (SSR)</strong> : SEO optimisé, performances maximales</li>
								<li><strong>Authentification avancée</strong> : OAuth2, JWT, SSO, 2FA, biométrie</li>
								<li><strong>File d'attente et jobs</strong> : RabbitMQ, Redis Queue, traitement asynchrone</li>
								<li><strong>Caching intelligent</strong> : CDN, Redis, stratégies de cache multicouches</li>
								<li><strong>Tests automatisés</strong> : Tests unitaires, intégration, E2E, CI/CD</li>
							</ul>

							<h3>Cas d'usage concrets</h3>
							<ul className='no-prefix'>
								<li>Plateforme de réservation avec disponibilités temps réel</li>
								<li>Application de gestion documentaire avec workflow complexe</li>
								<li>Solution de e-learning avec vidéo streaming et quiz interactifs</li>
								<li>Outil de business intelligence avec tableaux de bord personnalisables</li>
								<li>Application IoT pour monitoring d'équipements industriels</li>
								<li>Réseau social d'entreprise avec messagerie instantanée</li>
							</ul>
						</div>

						<aside className='content-sidebar'>
							<div className='sidebar-box'>
								<h3>Nos atouts</h3>
								<ul className='benefits-list no-prefix'>
									<li>Architecture scalable</li>
									<li>Code maintenable et testé</li>
									<li>Documentation complète</li>
									<li>Méthodologie Agile</li>
									<li>DevOps intégré</li>
									<li>Support post-livraison</li>
								</ul>
							</div>

							<div className='sidebar-box'>
								<h3>Budget projets</h3>
								<p className='price-info'>
									<strong>15 000€ - 50 000€ HT</strong><br />
									Application web standard
								</p>
								<p className='small'>
									MVP : à partir de 8 000€<br />
									Projets complexes : sur devis
								</p>
								<br />
								<p className='price-info'>
									<strong>TJM : 700-800€ HT</strong><br />
									Développeur senior full-stack
								</p>
							</div>

							<div className='sidebar-box'>
								<h3>Garanties projet</h3>
								<ul className='benefits-list no-prefix'>
									<li>Sprints de 2 semaines</li>
									<li>Démos régulières</li>
									<li>Code source versionnné</li>
									<li>Tests &gt; 80% coverage</li>
									<li>Garantie 6 mois</li>
								</ul>
							</div>

							<div className='sidebar-box cta-box'>
								<h3>Étude de faisabilité</h3>
								<p>Analysons ensemble la complexité technique de votre projet</p>
								<Button to={`${process.env.PUBLIC_URL}/nous-contacter`} version='1' size='small'>
									Planifier un call
								</Button>
							</div>
						</aside>
					</div>
				</div>
			</section>

			<section className='service-process'>
				<div className='container'>
					<h2 className='align-center'>Méthodologie de développement</h2>
					<div className='process-steps'>
						<div className='process-step'>
							<span className='step-number'>01</span>
							<h3>Discovery</h3>
							<p>Analyse fonctionnelle, architecture technique, planning</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>02</span>
							<h3>MVP</h3>
							<p>Développement des fonctionnalités core en mode agile</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>03</span>
							<h3>Itérations</h3>
							<p>Sprints successifs, feedback continu, ajustements</p>
						</div>
						<div className='process-step'>
							<span className='step-number'>04</span>
							<h3>Production</h3>
							<p>Déploiement, monitoring, maintenance évolutive</p>
						</div>
					</div>
				</div>
			</section>

			<ServicesList currentService='applications-web' />

			<div className='column-full align-center'>
				<Button to={`${process.env.PUBLIC_URL}/nous-contacter`} version='1'>
					Discuter de votre projet
				</Button>
			</div>
		</motion.main>
	)
}

export default ApplicationsWeb